import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Protocol, PhaseKind } from '../data/protocols';
import { ProtocolAudio, AudioExtras } from '../services/protocolAudio';
import { advance, isTimeComplete } from '../services/protocolSequencer';
import { ticksToFire } from '../services/runFormat';
import { useLanguage } from '../i18n/LanguageContext';
import RunnerControls from './RunnerControls';
import RunSurface from './RunSurface';
import BreathOrb from './BreathOrb';
import { AURORA, PHASE_ORB, SETTLE_ORB } from '../data/runTheme';

interface ProtocolRunnerProps {
  protocol: Protocol;
  soundEnabled: boolean;
  cuesEnabled: boolean;
  volume: number;
  extras: AudioExtras;
  audioCtx?: AudioContext | null;
  onComplete: () => void;
  onQuit: () => void;
  onPauseChange?: (paused: boolean) => void;
}

const easeInOutSine = (t: number): number => -(Math.cos(Math.PI * t) - 1) / 2;

const ProtocolRunner: React.FC<ProtocolRunnerProps> = ({
  protocol,
  soundEnabled,
  cuesEnabled,
  volume,
  extras,
  audioCtx,
  onComplete,
  onQuit,
  onPauseChange,
}) => {
  const { language, t } = useLanguage();
  const pick = (b: { no: string; en: string }) => (language === 'en' ? b.en : b.no);
  const markPhase = (kind: PhaseKind) => {
    if (cuesEnabled) audioRef.current?.cue(kind);
    else audioRef.current?.bell();
  };

  const [phaseIndex, setPhaseIndex] = useState(0);
  const [cycle, setCycle] = useState(0);
  const [phaseTime, setPhaseTime] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [inSettle, setInSettle] = useState(false);
  const [settleTime, setSettleTime] = useState(0);
  const [paused, setPaused] = useState(false);

  const audioRef = useRef<ProtocolAudio | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number>(0);

  // Refs mirror state so the rAF callback reads fresh values without re-subscribing.
  const phaseIndexRef = useRef(0);
  const cycleRef = useRef(0);
  const phaseTimeRef = useRef(0);
  const elapsedRef = useRef(0);
  const inSettleRef = useRef(false);
  const settleTimeRef = useRef(0);
  const doneRef = useRef(false);
  const pausedRef = useRef(false);

  // Mirrors the `extras` prop so the rAF closure below reads the current counting mode.
  const extrasRef = useRef(extras);
  extrasRef.current = extras;

  const phases = protocol.phases;

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    audioRef.current?.stop();
    audioRef.current = null;
    onComplete();
  }, [onComplete]);

  // User-initiated exit: tear down like finish(), but return to the menu
  // instead of advancing to the next segment / completion screen.
  const quit = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    audioRef.current?.stop();
    audioRef.current = null;
    onQuit();
  }, [onQuit]);

  const tick = useCallback(
    (ts: number) => {
      const delta = lastTsRef.current ? (ts - lastTsRef.current) / 1000 : 0;
      lastTsRef.current = ts;

      if (inSettleRef.current) {
        settleTimeRef.current += delta;
        setSettleTime(settleTimeRef.current);
        const total = protocol.settle?.seconds ?? 0;
        if (settleTimeRef.current >= total) {
          audioRef.current?.bell();
          finish();
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      elapsedRef.current += delta;
      setElapsed(elapsedRef.current);

      // Time-based protocols (Coherence) complete on elapsed time.
      if (isTimeComplete(protocol, elapsedRef.current)) {
        enterSettleOrFinish();
        return;
      }

      const phase = phases[phaseIndexRef.current];
      const prevPhaseTime = phaseTimeRef.current;
      phaseTimeRef.current += delta;
      const progress = Math.min(phaseTimeRef.current / phase.seconds, 1);
      audioRef.current?.setPhase(phase.kind, progress);
      setPhaseTime(phaseTimeRef.current);

      const counting = extrasRef.current.counting;
      if (counting && counting !== 'off') {
        const dur = phase.seconds;
        const marks = ticksToFire(prevPhaseTime, phaseTimeRef.current, dur);
        for (const s of marks) {
          audioRef.current?.tick();
          if (counting === 'countWarn' && s === dur - 1) audioRef.current?.changeTone();
        }
      }

      if (phaseTimeRef.current >= phase.seconds) {
        const { position, cyclesDone } = advance(protocol, {
          phaseIndex: phaseIndexRef.current,
          cycle: cycleRef.current,
        });
        if (cyclesDone) {
          enterSettleOrFinish();
          return;
        }
        phaseIndexRef.current = position.phaseIndex;
        cycleRef.current = position.cycle;
        phaseTimeRef.current = 0;
        setPhaseIndex(position.phaseIndex);
        setCycle(position.cycle);
        setPhaseTime(0);
        // Cue the new phase's action word exactly at the transition.
        markPhase(phases[position.phaseIndex].kind);
      }

      rafRef.current = requestAnimationFrame(tick);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [protocol, finish]
  );

  const enterSettleOrFinish = useCallback(() => {
    if (protocol.settle) {
      inSettleRef.current = true;
      setInSettle(true);
      audioRef.current?.settleDrone(protocol.sound.settleBeatHz);
      audioRef.current?.bell();
      rafRef.current = requestAnimationFrame(tick);
    } else {
      finish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocol, finish, tick]);

  const handlePause = useCallback(() => {
    if (doneRef.current || pausedRef.current) return;
    pausedRef.current = true;
    setPaused(true);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    audioRef.current?.pause();
    onPauseChange?.(true);
  }, [onPauseChange]);

  const handleResume = useCallback(() => {
    if (!pausedRef.current) return;
    pausedRef.current = false;
    setPaused(false);
    lastTsRef.current = 0;
    audioRef.current?.resume();
    onPauseChange?.(false);
    rafRef.current = requestAnimationFrame(tick);
  }, [tick, onPauseChange]);

  useEffect(() => {
    const audio = new ProtocolAudio(soundEnabled, volume, cuesEnabled, extras, audioCtx ?? null);
    audio.start(protocol.sound);
    audioRef.current = audio;
    if (cuesEnabled) audio.cue(phases[0].kind);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      audioRef.current?.stop();
      audioRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const phase = phases[phaseIndex];
  const phaseColor = inSettle ? SETTLE_ORB : PHASE_ORB[phase.kind];
  const breathSize = (() => {
    if (inSettle) return 0.5;
    const p = Math.min(phaseTime / phase.seconds, 1);
    switch (phase.kind) {
      case 'inhale':
        return easeInOutSine(p);
      case 'holdIn':
        return 1;
      case 'exhale':
        return 1 - easeInOutSine(p);
      case 'holdOut':
        return 0;
    }
  })();
  const scale = 0.4 + breathSize * 0.6;

  const remaining = inSettle
    ? Math.ceil((protocol.settle?.seconds ?? 0) - settleTime)
    : Math.ceil(phase.seconds - phaseTime);
  const ringProgress = inSettle ? 0 : Math.min(phaseTime / phase.seconds, 1);

  const cyclesLabel = protocol.cycles
    ? `${t('protocols.cyclesWord')} ${Math.min(cycle + 1, protocol.cycles)} / ${protocol.cycles}`
    : `${Math.floor(elapsed / 60)}:${String(Math.floor(elapsed % 60)).padStart(2, '0')}`;

  return (
    <RunSurface>
      <BreathOrb
        scale={scale}
        color={phaseColor}
        progress={ringProgress}
        label={inSettle ? t('protocols.naturalBreath') : pick(phase.label)}
        count={remaining}
        showRing={!inSettle}
      />
      <p style={{ textAlign: 'center', maxWidth: '28rem', margin: '1.5rem 1rem 0.75rem', minHeight: '3rem', padding: '0 1rem', color: AURORA.textSecondary }}>
        {inSettle ? pick(protocol.settle!.instruction) : pick(phase.instruction)}
      </p>
      <p style={{ fontSize: '0.875rem', color: AURORA.textMuted, marginBottom: '1.5rem' }}>{cyclesLabel}</p>
      <RunnerControls
        paused={paused}
        onPause={handlePause}
        onResume={handleResume}
        onStop={quit}
      />
    </RunSurface>
  );
};

export default ProtocolRunner;
